import React, { useState } from "react";

import { formatNumber, useTranslation } from "utils";

import { FieldListField } from "models";

import { AlertBanner, Flex, Icon, Link, TagNormal, Typography } from "components";

import { FieldMap } from "pages/app/historical-practice-data-table-view/components/FieldMap";
import { BULK_DATA_DRAWER_MODE, type BulkDataDrawerMode } from "pages/app/historical-practice-data-table-view/utils/BulkDataPointUtils";

export type BulkVerifiedField = {
  cropId: string;
  fieldId: string;
  // the locked value, where the data point has one worth showing (yield does, a practice list doesn't)
  valueLabel?: string;
};

export type BulkDataPointSectionProps = {
  // heads the panel the section draws around the inputs. Omitted for the data points whose form brings its own panel
  label?: string;
  mode: BulkDataDrawerMode;
  // what the save will do to the selection. Add mode states it up front, since "only the empty ones" changes how the
  // farmer reads the inputs; edit mode keeps it as a footnote under them
  explainer: string;
  verifiedFields: BulkVerifiedField[];
  fields: FieldListField[];
  children: React.ReactNode;
};

// the panel under the data point cards: what the save does to the selection, the inputs, and — collapsed by
// default — the crops this save will skip because their value is closed for editing
export const BulkDataPointSection = ({ label, mode, explainer, verifiedFields, fields, children }: BulkDataPointSectionProps) => {
  const { t } = useTranslation(["historicalPracticeDataTableView", "farm"]);
  const [areVerifiedFieldsShown, setAreVerifiedFieldsShown] = useState<boolean>(false);

  const editExplainer = mode === BULK_DATA_DRAWER_MODE.edit && (
    <Typography variant="paragraph-s" color="grey-600" className="margin-0" data-testid="bulk-data-point-section-explainer">
      {explainer}
    </Typography>
  );

  const verifiedSection = verifiedFields.length > 0 && (
    <Flex vertical gap="0.5rem">
      {/* both class names on purpose: Link drops its `type` class as soon as a className is passed */}
      <Link
        dataTestId="bulk-data-point-section-verified-toggle"
        className="primary-link bulk-data-point-section-verified-toggle"
        onClick={() => setAreVerifiedFieldsShown((wereShown) => !wereShown)}
      >
        <Flex align="center" gap="0.25rem">
          {t(
            areVerifiedFieldsShown
              ? "historicalPracticeDataTableView:bulkDataDrawer.hideVerified"
              : "historicalPracticeDataTableView:bulkDataDrawer.showVerified"
          )}
          <Icon iconName={areVerifiedFieldsShown ? "tabler/chevron-up" : "tabler/chevron-down"} size="xs" />
        </Flex>
      </Link>
      {areVerifiedFieldsShown &&
        verifiedFields.map((verifiedField) => {
          const field = fields.find((candidate) => candidate.id === verifiedField.fieldId);

          return (
            <Flex key={verifiedField.cropId} align="center" justify="space-between" gap="0.75rem" className="bulk-data-point-section-verified-row">
              <Flex align="center" gap="0.75rem" className="bulk-data-point-section-verified-field">
                {field && <FieldMap field={field} />}
                <Typography variant="paragraph-m" bold className="margin-0">
                  {field?.name || t("farm:unknownFieldName")}
                </Typography>
                <Typography variant="paragraph-s" color="grey-600" className="margin-0 no-wrap">
                  {formatNumber({ number: field?.size ?? 0 })} ha
                </Typography>
              </Flex>
              {verifiedField.valueLabel ? (
                // the locked value carries the data point it belongs to, e.g. "Yield: 3.50 t/ha"
                <TagNormal
                  className="bulk-data-point-section-verified-value"
                  icon="tabler/lock"
                  text={label ? `${label}: ${verifiedField.valueLabel}` : verifiedField.valueLabel}
                />
              ) : (
                <Icon iconName="tabler/lock" size="xs" className="bulk-data-point-section-verified-lock" />
              )}
            </Flex>
          );
        })}
    </Flex>
  );

  return (
    <Flex vertical gap="1rem" className="bulk-data-point-section" data-testid="bulk-data-point-section">
      {mode === BULK_DATA_DRAWER_MODE.add && (
        <AlertBanner
          dataTestId="bulk-data-point-section-explainer"
          type="warning"
          iconName="tabler/alert-circle"
          iconSize={24}
          iconAlign="start"
          description={explainer}
          descriptionSize="paragraph-m"
        />
      )}
      {/* the section's own panel holds the whole group — inputs, what the save will do, and the skipped crops — as one
          block. The forms that head their own panel keep the skipped crops in a panel of their own instead */}
      {label ? (
        <Flex vertical gap="0.75rem" className="bulk-data-point-panel">
          <Typography variant="h5">{label}</Typography>
          {children}
          {editExplainer}
          {verifiedSection}
        </Flex>
      ) : (
        <>
          {children}
          {editExplainer}
          {verifiedSection && (
            <Flex vertical className="bulk-data-point-panel">
              {verifiedSection}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
